import { useRef, useState } from 'react';

export type DocumentUploadAttempt = {
  id: string;
  file: File;
} & ({ status: 'uploading' | 'failed' } | { status: 'uploaded'; documentId: string });

export function useDocumentUploadQueue({
  upload,
  onUploaded,
}: {
  upload: (file: File) => Promise<{ key: string }>;
  onUploaded: (documentIds: string[]) => void;
}) {
  const [attempts, setAttempts] = useState<DocumentUploadAttempt[]>([]);
  const activeAttemptIds = useRef(new Set<string>());

  const run = async (requestedAttempts: DocumentUploadAttempt[]) => {
    const pendingAttempts = requestedAttempts.filter(({ id }) => !activeAttemptIds.current.has(id));
    for (const { id } of pendingAttempts) activeAttemptIds.current.add(id);
    setAttempts(previous =>
      previous.map(attempt =>
        pendingAttempts.some(({ id }) => id === attempt.id)
          ? { id: attempt.id, file: attempt.file, status: 'uploading' }
          : attempt,
      ),
    );

    const results = await Promise.all(
      pendingAttempts.map(async attempt => {
        try {
          const { key } = await upload(attempt.file);
          setAttempts(previous =>
            previous.map(item =>
              item.id === attempt.id ? { ...attempt, status: 'uploaded', documentId: key } : item,
            ),
          );
          return key;
        } catch {
          setAttempts(previous =>
            previous.map(item =>
              item.id === attempt.id ? { ...attempt, status: 'failed' } : item,
            ),
          );
          return undefined;
        } finally {
          activeAttemptIds.current.delete(attempt.id);
        }
      }),
    );
    const documentIds = results.filter(
      (documentId): documentId is string => documentId !== undefined,
    );
    if (documentIds.length > 0) onUploaded(documentIds);
  };

  const addFiles = async (files: File[]) => {
    const newAttempts = files.map(
      (file): DocumentUploadAttempt => ({
        id: crypto.randomUUID(),
        file,
        status: 'uploading',
      }),
    );
    setAttempts(previous => [...previous, ...newAttempts]);
    await run(newAttempts);
  };

  return {
    attempts,
    addFiles,
    isUploading: attempts.some(({ status }) => status === 'uploading'),
    retry: (id?: string) =>
      run(
        attempts.filter(
          attempt => attempt.status === 'failed' && (id === undefined || attempt.id === id),
        ),
      ),
    remove: (id: string) =>
      setAttempts(previous =>
        previous.filter(attempt => attempt.id !== id || attempt.status !== 'failed'),
      ),
  };
}
